import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { Loader2 } from 'lucide-react'
import { Skeleton } from '@/components/ui'
import { useDashboard } from '@/hooks/useDashboard'
import { useDashboardPropertyId } from '@/hooks/useDashboardScope'
import { useLocalizationConfig } from '@/hooks/useDateFormatter'
import { formatMonthYear, localToday } from '@/lib/date'
import { formatBaht } from '@/lib/money'

/**
 * EP-155: the owner's figures, two rows of four compact tiles (label over
 * value), each a link to its report. Overdue is not here: it is a row of the
 * to-do card with its amount. The rent roll and the vacancy gap, a block of
 * their own before, are the second row with occupancy and deposits held.
 *
 * Every figure is the server's; the collection rate is the same display ratio
 * of two server figures BusinessSnapshot shows (EP-117: one invoice
 * population, never revenue_this_month over billed).
 */

interface Tile {
  id: string
  label: string
  value: string
  sub?: string
  to?: string
  tone?: 'success' | 'error'
}

export function OwnerMoney() {
  const { t, i18n } = useTranslation('common')
  const { timezone, calendar } = useLocalizationConfig()
  const propertyId = useDashboardPropertyId()
  const { data: stats, isLoading, isFetching } = useDashboard(propertyId)
  const today = localToday(timezone)
  const period = formatMonthYear(new Date(`${today.slice(0, 7)}-01T00:00:00`), { locale: i18n.language, calendar })

  const r = stats?.revenue
  const billed = r?.billed_this_month ?? 0
  const collectedOnBilled = r?.collected_on_billed_this_month
  const rate = r && collectedOnBilled !== undefined && billed > 0
    ? Math.round((collectedOnBilled / billed) * 1000) / 10
    : null
  const money = (n: number | undefined) => formatBaht(n, { decimals: 0 })
  const netProfit = r?.net_profit_this_month ?? 0

  const tiles: Tile[] = [
    { id: 'money-billed', label: t('dashboard.money.billed'), value: billed === 0 ? '—' : money(billed), to: '/reports/collections/stats' },
    {
      id: 'money-received', label: t('dashboard.money.received'), value: money(r?.revenue_this_month),
      sub: rate === null ? undefined : t('dashboard.money.received_rate', { rate }), to: '/reports/revenue', tone: 'success',
    },
    { id: 'money-unpaid', label: t('dashboard.money.unpaid'), value: money(r?.unpaid_this_month), to: '/reports/collections/stats' },
    { id: 'money-net-profit', label: t('dashboard.money.net_profit'), value: money(netProfit), to: '/reports/profitability/pl', tone: netProfit >= 0 ? 'success' : 'error' },
    { id: 'money-occupancy', label: t('dashboard.money.occupancy'), value: stats ? `${stats.occupancy_rate}%` : '—', to: '/reports/occupancy/stats' },
    { id: 'money-rent-roll', label: t('dashboard.money.rent_roll'), value: r?.rent_roll === undefined ? '—' : money(r.rent_roll), to: '/reports/occupancy' },
    { id: 'money-vacancy-gap', label: t('dashboard.money.vacancy_gap'), value: r?.vacancy_gap === undefined ? '—' : money(r.vacancy_gap), to: '/reports/occupancy/vacant' },
    { id: 'money-deposits-held', label: t('dashboard.money.deposits_held'), value: stats?.deposits_held?.total === undefined ? '—' : money(stats.deposits_held.total) },
  ]

  return (
    <section className="space-y-2" data-testid="owner-money" aria-labelledby="owner-money-title">
      <div className="flex items-center gap-2">
        <h2 id="owner-money-title" className="text-sm font-semibold text-base-content/80">{t('dashboard.business_snapshot_title')}</h2>
        <span className="text-xs text-base-content/60">{t('dashboard.money.period', { period })}</span>
        {isFetching && !isLoading && (
          <Loader2 className="w-3.5 h-3.5 animate-spin text-base-content/40" aria-label={t('labels.refreshing')} />
        )}
        <Link to="/reports" className="ml-auto text-xs text-primary hover:underline whitespace-nowrap" data-testid="owner-money-reports">
          {t('dashboard.view_all_reports')} →
        </Link>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
        {tiles.map((tile) => {
          const body = (
            <>
              <p className="text-xs text-base-content/60 truncate">{tile.label}</p>
              {isLoading ? (
                <Skeleton className="h-5 w-20 mt-1" />
              ) : (
                <p className={`text-base font-bold tabular-nums truncate ${tile.tone === 'success' ? 'text-success' : tile.tone === 'error' ? 'text-error' : ''}`}>
                  {tile.value}
                </p>
              )}
              {tile.sub && !isLoading && <p className="text-xs text-base-content/60 truncate">{tile.sub}</p>}
            </>
          )
          const className = 'block rounded-lg border border-base-200 bg-base-100 px-3 py-2 min-w-0'
          return tile.to ? (
            <Link key={tile.id} to={tile.to} className={`${className} no-underline text-base-content hover:bg-base-200 transition-colors`}
              data-testid={tile.id} aria-label={`${tile.label}: ${tile.value}${tile.sub ? `, ${tile.sub}` : ''}`}>
              {body}
            </Link>
          ) : (
            <div key={tile.id} className={className} data-testid={tile.id}>{body}</div>
          )
        })}
      </div>
    </section>
  )
}
